import * as Yup from 'yup';
import appConfig from '../../../config/appConfig';

export const EMAIL_SETTINGS_QUERY_KEY = ['organization-email-settings'];

export const EMAIL_PROVIDER_SMTP = 'smtp';
export const EMAIL_PROVIDER_MICROSOFT_GRAPH = 'microsoft_graph';
export const GRAPH_MODE_APP_REGISTRATION = 'app_registration';
export const GRAPH_STATUS_EXPIRED = 'expired';

export const ENTRA_APP_REGISTRATION_GUIDE_URL = `${appConfig.docsBaseUrl}/docs/product-wiki/configure-microsoft-365-email`;

export const MIN_SMTP_PORT = 1;
export const MAX_SMTP_PORT = 65535;

export const DEFAULT_EMAIL_SETTINGS_FORM = {
  enabled: false,
  provider: EMAIL_PROVIDER_SMTP,
  from_email: '',
  from_name: '',
  host: '',
  port: '465',
  username: '',
  password: '',
  tls: true,
  tenant_id: '',
  client_id: '',
  client_secret: '',
};

export function mapEmailSettingsToForm(data) {
  if (!data) {
    return { ...DEFAULT_EMAIL_SETTINGS_FORM };
  }

  const smtp = data.smtp ?? {};
  const graph = data.graph ?? {};

  return {
    enabled: Boolean(data.enabled),
    provider: data.provider || EMAIL_PROVIDER_SMTP,
    from_email: data.from_email ?? '',
    from_name: data.from_name ?? '',
    host: smtp.host ?? '',
    port: smtp.port != null && String(smtp.port).trim() !== '' ? String(smtp.port) : '465',
    username: smtp.username ?? '',
    password: smtp.password ?? '',
    tls: smtp.tls != null ? Boolean(smtp.tls) : true,
    tenant_id: graph.tenant_id ?? '',
    client_id: graph.client_id ?? '',
    client_secret: '',
  };
}

export function hasStoredPassword(data) {
  return Boolean(data?.smtp?.password);
}

export function hasStoredClientSecret(data) {
  return Boolean(data?.graph?.client_secret_set || data?.graph?.client_secret);
}

export function clientSecretRequired(data, values) {
  if (!hasStoredClientSecret(data)) {
    return true;
  }

  const stored = data?.graph ?? {};
  return (
    (values.tenant_id || '').trim() !== (stored.tenant_id || '') ||
    (values.client_id || '').trim() !== (stored.client_id || '')
  );
}

export function buildEmailSettingsPayload(values, initialPassword = '', initialGraph = null) {
  if (!values.enabled) {
    return { enabled: false };
  }

  const fromEmail = values.from_email.trim();
  const fromName = values.from_name.trim();

  if (values.provider === EMAIL_PROVIDER_MICROSOFT_GRAPH) {
    const tenantId = values.tenant_id.trim();
    const clientId = values.client_id.trim();
    const clientSecret = values.client_secret || '';
    const secretUnchanged =
      initialGraph &&
      clientSecret === (initialGraph.client_secret || '') &&
      tenantId === (initialGraph.tenant_id || '').trim() &&
      clientId === (initialGraph.client_id || '').trim();

    return {
      enabled: true,
      provider: EMAIL_PROVIDER_MICROSOFT_GRAPH,
      from_email: fromEmail,
      from_name: fromName,
      graph: {
        mode: GRAPH_MODE_APP_REGISTRATION,
        tenant_id: tenantId,
        client_id: clientId,
        client_secret: secretUnchanged ? '' : clientSecret,
      },
    };
  }

  const password = values.password === initialPassword ? '' : values.password;

  return {
    enabled: true,
    provider: EMAIL_PROVIDER_SMTP,
    from_email: fromEmail,
    from_name: fromName,
    smtp: {
      host: values.host.trim(),
      port: Number(values.port),
      username: values.username.trim(),
      password,
      tls: Boolean(values.tls),
    },
  };
}

function isSmtp(enabled, provider) {
  return Boolean(enabled) && provider !== EMAIL_PROVIDER_MICROSOFT_GRAPH;
}

function isMicrosoft(enabled, provider) {
  return Boolean(enabled) && provider === EMAIL_PROVIDER_MICROSOFT_GRAPH;
}

export function buildEmailSettingsSchema({ passwordRequired, clientSecretRequired: secretRequired } = {}) {
  return Yup.object().shape({
    enabled: Yup.boolean().required(),
    provider: Yup.string().oneOf([EMAIL_PROVIDER_SMTP, EMAIL_PROVIDER_MICROSOFT_GRAPH]).required(),
    from_email: Yup.string().when('enabled', {
      is: true,
      then: (schema) => schema.trim().required('Sender email address is required').email('Enter a valid email address'),
      otherwise: (schema) => schema.notRequired(),
    }),
    from_name: Yup.string().when('enabled', {
      is: true,
      then: (schema) => schema.trim().required('Sender name is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    host: Yup.string().when(['enabled', 'provider'], {
      is: isSmtp,
      then: (schema) => schema.trim().required('SMTP server is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    port: Yup.mixed().when(['enabled', 'provider'], {
      is: isSmtp,
      then: () =>
        Yup.number()
          .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
          .typeError('Port must be a number')
          .integer('Port must be a whole number')
          .min(MIN_SMTP_PORT, `Port must be between ${MIN_SMTP_PORT} and ${MAX_SMTP_PORT}`)
          .max(MAX_SMTP_PORT, `Port must be between ${MIN_SMTP_PORT} and ${MAX_SMTP_PORT}`)
          .required('SMTP port is required'),
      otherwise: () => Yup.mixed().notRequired(),
    }),
    username: Yup.string().when(['enabled', 'provider'], {
      is: isSmtp,
      then: (schema) => schema.trim().required('SMTP username is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    password: Yup.string().when(['enabled', 'provider'], {
      is: (enabled, provider) => isSmtp(enabled, provider) && passwordRequired,
      then: (schema) => schema.required('SMTP password is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    tls: Yup.boolean().required(),
    tenant_id: Yup.string().when(['enabled', 'provider'], {
      is: isMicrosoft,
      then: (schema) => schema.trim().required('Directory (tenant) ID is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    client_id: Yup.string().when(['enabled', 'provider'], {
      is: isMicrosoft,
      then: (schema) => schema.trim().required('Application (client) ID is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    client_secret: Yup.string().when(['enabled', 'provider'], {
      is: (enabled, provider) => isMicrosoft(enabled, provider) && secretRequired,
      then: (schema) => schema.required('Client secret is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  });
}

export function formatProviderTestError(error) {
  const errors = error?.response?.data?.errors;
  if (!errors) {
    return error?.message || 'Connection test failed';
  }

  if (errors.code && errors.message) {
    return `${errors.code}: ${errors.message}`;
  }

  return errors.message || 'Connection test failed';
}
